import React, { useState, useRef, useEffect } from 'react';
import './PhonemePractice.css';
import PronunciationFeedback from './PronunciationFeedback';

interface PhonemePracticeProps {
  phoneme: string;
  onClose: () => void;
  language?: string;
  accent?: string;
}

interface Recording {
  url: string;
  timestamp: Date;
}

const PhonemePractice: React.FC<PhonemePracticeProps> = ({ 
  phoneme, 
  onClose,
  language = 'English',
  accent = 'American' 
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [userRecordings, setUserRecordings] = useState<Recording[]>([]);
  const [coachExamples, setCoachExamples] = useState<string[]>([]);
  const [isLoadingExamples, setIsLoadingExamples] = useState(false);
  const [playingUrl, setPlayingUrl] = useState<string>('');
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastFeedback, setLastFeedback] = useState<any>(null);
  // Removed tab state - now showing unified view
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  
  // Determine backend URL
  const backendUrl = window.location.hostname === 'localhost' 
    ? 'http://localhost:8080'
    : 'https://voice-coach-backend-406125116283.us-east1.run.app';

  useEffect(() => {
    fetchCoachExamples();
    
    // Cleanup function to stop audio when component unmounts or modal closes
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
        setPlayingUrl('');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneme, language, accent]);

  const fetchCoachExamples = async () => {
    try {
      setIsLoadingExamples(true);
      const response = await fetch(
        `${backendUrl}/api/phonemes/approved?language=${language}&accent=${accent}`
      );
      
      if (response.ok) {
        const data = await response.json();
        // Extract URLs for the specific phoneme
        const phonemeKey = phoneme.replace(/[\]\\[]/g, ''); // Remove brackets if any
        const urls = data[phoneme] || data[`/${phoneme}/`] || data[phonemeKey] || [];
        setCoachExamples(urls);
      }
    } catch (error) {
      console.error('Error fetching coach examples:', error);
    } finally {
      setIsLoadingExamples(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      // Show feedback component to start listening
      setShowFeedback(true);
      setLastFeedback(null);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setUserRecordings(prev => [...prev, { url: audioUrl, timestamp: new Date() }]);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording. Please check your microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      // Keep feedback visible after recording stops
    }
  };
  
  const handleFeedback = (feedback: any) => {
    setLastFeedback(feedback);
  };

  const playAudio = (url: string) => {
    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setPlayingUrl('');
    }
    
    // Create and play new audio
    const audio = new Audio(url);
    currentAudioRef.current = audio;
    setPlayingUrl(url);
    
    audio.onended = () => {
      setPlayingUrl('');
      currentAudioRef.current = null;
    };
    
    audio.onerror = (e) => {
      console.error('Audio playback error:', e);
      setPlayingUrl('');
      currentAudioRef.current = null;
      alert('Failed to play audio. The file may be unavailable.');
    };
    
    audio.play().catch(err => {
      console.error('Play failed:', err);
      setPlayingUrl('');
      currentAudioRef.current = null;
      alert('Failed to play audio. Please try again.');
    });
  };

  const deleteRecording = (index: number) => {
    setUserRecordings(prev => prev.filter((_, i) => i !== index));
  };

  // Generate AI pronunciation guide (simplified - in production, this would call an API)
  const generateAIPronunciation = () => {
    const guides: { [key: string]: string } = {
      '/i:/': 'Say "eee" as in "beat" - tongue high and forward, lips spread',
      '/ɪ/': 'Say "i" as in "bit" - tongue slightly lower than /i:/, lips relaxed',
      '/e/': 'Say "e" as in "bet" - tongue mid-front, jaw slightly open',
      '/æ/': 'Say "a" as in "bat" - tongue low-front, jaw open',
      '/ɑ:/': 'Say "ah" as in "father" - tongue low-back, jaw fully open',
      '/ɔ:/': 'Say "aw" as in "bought" - tongue low-back, lips rounded',
      '/u:/': 'Say "oo" as in "boot" - tongue high-back, lips rounded',
      '/ʊ/': 'Say "u" as in "put" - tongue slightly lower than /u:/, lips less rounded',
      '/s/': 'Hiss like a snake - tongue tip near upper teeth, air flows through center',
      '/z/': 'Buzz like a bee - same as /s/ but with voice',
      '/θ/': 'Say "th" as in "think" - tongue between teeth, no voice',
      '/ð/': 'Say "th" as in "this" - tongue between teeth, with voice',
      '/ʃ/': 'Say "sh" as in "ship" - tongue blade raised, lips slightly rounded',
      '/ʒ/': 'Say "zh" as in "measure" - same as /ʃ/ but with voice',
      '/r/': 'Say "r" as in "red" - tongue tip curled back or bunched',
      '/l/': 'Say "l" as in "let" - tongue tip touches upper teeth ridge',
      '/w/': 'Say "w" as in "wet" - lips rounded, then spread',
      '/j/': 'Say "y" as in "yes" - tongue high-front, then moves',
    };

    // Clean up phoneme format for lookup
    const cleanPhoneme = phoneme.trim();
    return guides[cleanPhoneme] || `Practice the ${phoneme} sound with clear articulation`;
  };

  return (
    <div className="phoneme-practice-modal">
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-content">
        <div className="modal-header">
          <h2>Practice: {phoneme}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>


        <div className="modal-body">
          <div className="unified-practice-section">
            <div className="pronunciation-guide-section">
              <div className="ai-guide">
                <h3>📖 Pronunciation Guide</h3>
                <p className="pronunciation-text">{generateAIPronunciation()}</p>
              </div>

              <div className="coach-examples">
                <h3>🎧 Coach Examples</h3>
                {isLoadingExamples ? (
                  <p className="loading">Loading examples...</p>
                ) : coachExamples.length > 0 ? (
                  <div className="examples-container">
                    <div className="example-controls">
                      <button
                        className={`main-play-btn ${playingUrl === coachExamples[currentExampleIndex] ? 'playing' : ''}`}
                        onClick={() => {
                          setCurrentExampleIndex(currentExampleIndex);
                          playAudio(coachExamples[currentExampleIndex]);
                        }}
                      >
                        {playingUrl === coachExamples[currentExampleIndex] ? '🔊 Playing...' : '▶️ Play Example'}
                      </button>
                      
                      {coachExamples.length > 1 && (
                        <div className="example-navigation">
                          <button
                            className="nav-btn"
                            onClick={() => {
                              const newIndex = (currentExampleIndex - 1 + coachExamples.length) % coachExamples.length;
                              setCurrentExampleIndex(newIndex);
                              if (playingUrl) playAudio(coachExamples[newIndex]);
                            }}
                            title="Previous example"
                          >
                            ◀
                          </button>
                          <span className="example-counter">
                            {currentExampleIndex + 1} / {coachExamples.length}
                          </span>
                          <button
                            className="nav-btn"
                            onClick={() => {
                              const newIndex = (currentExampleIndex + 1) % coachExamples.length;
                              setCurrentExampleIndex(newIndex);
                              if (playingUrl) playAudio(coachExamples[newIndex]);
                            }}
                            title="Next example"
                          >
                            ▶
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {coachExamples.length > 1 && (
                      <div className="example-list">
                        {coachExamples.map((url, index) => (
                          <button
                            key={index}
                            className={`example-btn ${currentExampleIndex === index ? 'selected' : ''} ${playingUrl === url ? 'playing' : ''}`}
                            onClick={() => {
                              setCurrentExampleIndex(index);
                              playAudio(url);
                            }}
                          >
                            {index + 1}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="no-examples">No coach recordings available yet</p>
                )}
              </div>
            </div>

            <div className="practice-recording-section">
              <h3>🎯 Practice Now</h3>
              <div className="practice-instructions">
                <ol>
                  <li>Listen to the coach examples above</li>
                  <li>Click "Start Recording" and say the {phoneme} sound</li>
                  <li>Stop recording and listen to your attempt</li>
                  <li>Compare with the examples and try again!</li>
                </ol>
              </div>

              <div className="record-controls">
                {!isRecording ? (
                  <button className="record-btn start" onClick={startRecording}>
                    🔴 Start Recording
                  </button>
                ) : (
                  <button className="record-btn stop" onClick={stopRecording}>
                    ⏹️ Stop Recording
                  </button>
                )}
              </div>

              {/* Pronunciation Feedback */}
              {showFeedback && (
                <PronunciationFeedback
                  targetPhoneme={phoneme}
                  isRecording={isRecording}
                  onFeedback={handleFeedback}
                />
              )}

              <div className="user-recordings">
                <h4>Your Practice Recordings</h4>
                {userRecordings.length === 0 ? (
                  <p className="no-recordings">No recordings yet. Start practicing!</p>
                ) : (
                  <div className="recordings-list">
                    {userRecordings.map((recording, index) => (
                      <div key={index} className="recording-item">
                        <span className="recording-label">
                          Attempt {index + 1} - {recording.timestamp.toLocaleTimeString()}
                        </span>
                        <div className="recording-controls">
                          <button
                            className={`play-btn ${playingUrl === recording.url ? 'playing' : ''}`}
                            onClick={() => playAudio(recording.url)}
                            disabled={playingUrl === recording.url}
                          >
                            {playingUrl === recording.url ? '🔊' : '▶️'}
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => deleteRecording(index)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="practice-tips">
                <h4>💡 Tips for {phoneme}</h4>
                <ul>
                  <li>Record in a quiet environment</li>
                  <li>Speak clearly and at a natural pace</li>
                  <li>Focus on the specific mouth shape and tongue position</li>
                  <li>Practice multiple times to improve</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhonemePractice;